import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AdminLayout } from '../components/AdminLayout';
import { useToast } from '../contexts/ToastContext';
import { api } from '../services/api';
import { applySeoConfig } from '../utils/seoConfig';
import { Copy, Save, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';

const Container = styled.div`
  padding: 2rem;
  width: 100%;
  max-width: none;
  margin: 0;

  @media (max-width: 768px) {
    padding: 1.25rem 1rem;
  }
`;

const Header = styled.div`
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    margin-bottom: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #1a202c;
  margin-bottom: 0.5rem;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const Subtitle = styled.p`
  color: #718096;
  font-size: 1rem;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  border-bottom: 2px solid #e2e8f0;

  @media (max-width: 768px) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 1rem 1.5rem;
  background: none;
  border: none;
  border-bottom: 3px solid ${props => props.active ? '#00d4aa' : 'transparent'};
  color: ${props => props.active ? '#00d4aa' : '#718096'};
  font-weight: ${props => props.active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: -2px;

  &:hover {
    color: #00d4aa;
  }

  @media (max-width: 768px) {
    padding: 0.75rem 1rem;
    white-space: nowrap;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    padding: 1.25rem;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 500;
  color: #4a5568;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.1);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 1rem;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #00d4aa;
    box-shadow: 0 0 0 3px rgba(0, 212, 170, 0.1);
  }
`;

const HelpText = styled.p`
  font-size: 0.875rem;
  color: #718096;
  margin-top: 0.5rem;
  line-height: 1.5;
`;

const CodeBlock = styled.pre`
  background: #1a202c;
  color: #e2e8f0;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  font-size: 0.875rem;
  margin-top: 0.5rem;
  position: relative;

  code {
    font-family: 'Courier New', monospace;
  }
`;

const CopyButton = styled.button`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  padding: 0.5rem;
  background: #2d3748;
  border: none;
  border-radius: 6px;
  color: #e2e8f0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;

  &:hover {
    background: #4a5568;
  }
`;

const Button = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: white;
  padding: 0.875rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 212, 170, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`;

const StatusCard = styled.div<{ status: 'success' | 'warning' | 'error' }>`
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  background: ${props =>
    props.status === 'success' ? '#f0fdf4' :
    props.status === 'warning' ? '#fffbeb' :
    '#fef2f2'
  };
  border-left: 4px solid ${props =>
    props.status === 'success' ? '#10b981' :
    props.status === 'warning' ? '#f59e0b' :
    '#ef4444'
  };
  color: ${props =>
    props.status === 'success' ? '#065f46' :
    props.status === 'warning' ? '#92400e' :
    '#991b1b'
  };

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
  margin-bottom: 1.5rem;
`;

const LinkButton = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: #00d4aa;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

interface SEOConfig {
  googleAnalyticsId: string;
  googleTagManagerId: string;
  googleSearchConsoleCode: string;
  microsoftClarityId: string;
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  ogTitle: string;
  ogDescription: string;
  ogImage: string;
  twitterCard: string;
}

const normalizeTrackingId = (value: string, type: 'ga' | 'gtm' | 'clarity') => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }

  if (type === 'ga') {
    const match = trimmed.match(/G-[A-Z0-9]+/i);
    return match ? match[0].toUpperCase() : trimmed;
  }

  if (type === 'gtm') {
    const match = trimmed.match(/GTM-[A-Z0-9]+/i);
    return match ? match[0].toUpperCase() : trimmed;
  }

  const clarityMatch =
    trimmed.match(/clarity\.ms\/tag\/([A-Za-z0-9]+)/i) ||
    trimmed.match(/"clarity"\s*,\s*"script"\s*,\s*"([A-Za-z0-9]+)"/i);
  return clarityMatch ? clarityMatch[1] : trimmed;
};

const normalizeSearchConsoleCode = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) {
    return '';
  }
  const match = trimmed.match(/content=["']([^"']+)["']/i);
  return match ? match[1] : trimmed;
};

export const AdminSEOPage: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const [activeTab, setActiveTab] = useState<'analytics' | 'meta-tags' | 'guide'>('analytics');
  const [config, setConfig] = useState<SEOConfig>({
    googleAnalyticsId: '',
    googleTagManagerId: '',
    googleSearchConsoleCode: '',
    microsoftClarityId: '',
    metaTitle: 'Ponto Fiel - Sistema de Fidelidade e Programa de Pontos para Empresas',
    metaDescription: 'Ponto Fiel - Sistema completo de fidelidade para sua empresa. Fidelize clientes, aumente vendas e crie programas de pontos, recompensas e cashback.',
    metaKeywords: 'programa de fidelidade, clube de pontos, fidelização de clientes, cashback, recompensas, sistema de pontos, crm, gestão de clientes',
    ogTitle: 'Ponto Fiel - Sistema de Fidelidade para Empresas',
    ogDescription: 'Fidelize clientes e aumente suas vendas com o sistema completo de pontos, recompensas e cashback. +2.500 empresas já usam!',
    ogImage: '/logo1.png',
    twitterCard: 'summary_large_image',
  });

  useEffect(() => {
    // Carregar configurações da API (rota pública)
    const loadConfig = async () => {
      try {
        const response = await api.get('/seo-config');
        if (response.data?.success && response.data?.data) {
          setConfig(response.data.data);
          applySeoConfig(response.data.data);
        }
      } catch (error) {
        console.error('Erro ao carregar configurações de SEO:', error);
      }
    };

    loadConfig();
  }, []);

  const handleSave = async () => {
    try {
      const adminToken = localStorage.getItem('admin_token');
      const authToken = localStorage.getItem('auth_token');
      const token = adminToken || authToken;

      if (!token || token === 'null') {
        showError('Token de autenticação inválido. Faça login novamente.');
        return;
      }

      // Salvar na API
      const response = await api.put('/admin/seo-config', config);
      const data = response.data;

      if (response.status === 200 && data?.success) {
        applySeoConfig(config);
        showSuccess('Configurações de SEO salvas com sucesso! Recarregue a página para aplicar todas as mudanças.');
      } else {
        showError('Erro ao salvar configurações: ' + (data?.message || 'Erro desconhecido'));
      }
    } catch (error) {
      console.error('Erro ao salvar configurações:', error);
      const message =
        (error as any)?.response?.data?.message ||
        'Erro ao salvar configurações. Tente novamente.';
      showError(message);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showSuccess('Código copiado!');
  };

  return (
    <AdminLayout>
      <Container>
        <Header>
          <Title>Configurações de SEO</Title>
          <Subtitle>Gerencie Google Analytics, Tag Manager, Search Console e meta tags</Subtitle>
        </Header>

        <TabContainer>
          <Tab active={activeTab === 'analytics'} onClick={() => setActiveTab('analytics')}>
            Analytics & Tracking
          </Tab>
          <Tab active={activeTab === 'meta-tags'} onClick={() => setActiveTab('meta-tags')}>
            Meta Tags
          </Tab>
          <Tab active={activeTab === 'guide'} onClick={() => setActiveTab('guide')}>
            Guia de Configuração
          </Tab>
        </TabContainer>

        {activeTab === 'analytics' && (
          <>
            <Section>
              <SectionTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Google Analytics
              </SectionTitle>

              {config.googleAnalyticsId ? (
                <StatusCard status="success">
                  <CheckCircle size={20} />
                  Google Analytics está configurado e ativo
                </StatusCard>
              ) : (
                <StatusCard status="warning">
                  <AlertCircle size={20} />
                  Configure o Google Analytics para rastrear visitantes
                </StatusCard>
              )}

              <FormGroup>
                <Label>ID de Rastreamento do Google Analytics (GA4)</Label>
                <Input
                  type="text"
                  placeholder="G-XXXXXXXXXX"
                  value={config.googleAnalyticsId}
                  onChange={(e) => setConfig({
                    ...config,
                    googleAnalyticsId: normalizeTrackingId(e.target.value, 'ga')
                  })}
                />
                <HelpText>
                  Cole aqui seu ID do Google Analytics 4 ou o script completo.
                  Exemplo: G-XXXXXXXXXX
                  <br />
                  <LinkButton href="https://analytics.google.com/" target="_blank">
                    Acessar Google Analytics <ExternalLink size={14} />
                  </LinkButton>
                </HelpText>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Google Tag Manager
              </SectionTitle>

              {config.googleTagManagerId ? (
                <StatusCard status="success">
                  <CheckCircle size={20} />
                  Google Tag Manager está configurado e ativo
                </StatusCard>
              ) : (
                <StatusCard status="warning">
                  <AlertCircle size={20} />
                  Configure o Tag Manager para gerenciar tags de marketing
                </StatusCard>
              )}

              <FormGroup>
                <Label>ID do Google Tag Manager</Label>
                <Input
                  type="text"
                  placeholder="GTM-XXXXXXX"
                  value={config.googleTagManagerId}
                  onChange={(e) => setConfig({
                    ...config,
                    googleTagManagerId: normalizeTrackingId(e.target.value, 'gtm')
                  })}
                />
                <HelpText>
                  Cole aqui seu ID do Tag Manager ou o script completo.
                  Exemplo: GTM-XXXXXXX
                  <br />
                  <LinkButton href="https://tagmanager.google.com/" target="_blank">
                    Acessar Tag Manager <ExternalLink size={14} />
                  </LinkButton>
                </HelpText>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Google Search Console
              </SectionTitle>

              {config.googleSearchConsoleCode ? (
                <StatusCard status="success">
                  <CheckCircle size={20} />
                  Site verificado no Google Search Console
                </StatusCard>
              ) : (
                <StatusCard status="warning">
                  <AlertCircle size={20} />
                  Verifique seu site no Google Search Console
                </StatusCard>
              )}

              <FormGroup>
                <Label>Código de Verificação do Search Console</Label>
                <Input
                  type="text"
                  placeholder="abc123def456..."
                  value={config.googleSearchConsoleCode}
                  onChange={(e) => setConfig({
                    ...config,
                    googleSearchConsoleCode: normalizeSearchConsoleCode(e.target.value)
                  })}
                />
                <HelpText>
                  Cole o codigo de verificacao ou a tag completa.
                  Exemplo: abc123def456ghi789
                  <br />
                  <LinkButton href="https://search.google.com/search-console" target="_blank">
                    Acessar Search Console <ExternalLink size={14} />
                  </LinkButton>
                </HelpText>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Microsoft Clarity
              </SectionTitle>

              {config.microsoftClarityId ? (
                <StatusCard status="success">
                  <CheckCircle size={20} />
                  Microsoft Clarity está configurado e rastreando comportamento dos usuários
                </StatusCard>
              ) : (
                <StatusCard status="warning">
                  <AlertCircle size={20} />
                  Configure o Clarity para ver mapas de calor e gravações de sessões
                </StatusCard>
              )}

              <FormGroup>
                <Label>ID do Microsoft Clarity</Label>
                <Input
                  type="text"
                  placeholder="xxxxxxxxxx"
                  value={config.microsoftClarityId}
                  onChange={(e) => setConfig({
                    ...config,
                    microsoftClarityId: normalizeTrackingId(e.target.value, 'clarity')
                  })}
                />
                <HelpText>
                  Cole aqui seu ID do Microsoft Clarity ou o script completo.
                  Exemplo: abc1de2fgh
                  <br />
                  <LinkButton href="https://clarity.microsoft.com/" target="_blank">
                    Acessar Microsoft Clarity <ExternalLink size={14} />
                  </LinkButton>
                </HelpText>
              </FormGroup>
            </Section>
          </>
        )}

        {activeTab === 'meta-tags' && (
          <>
            <Section>
              <SectionTitle>Meta Tags Básicas</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Título da Página (Title Tag)</Label>
                  <Input
                    type="text"
                    value={config.metaTitle}
                    onChange={(e) => setConfig({ ...config, metaTitle: e.target.value })}
                    maxLength={60}
                  />
                  <HelpText>Máximo de 60 caracteres. Aparece nas abas do navegador e resultados do Google.</HelpText>
                </FormGroup>

                <FormGroup>
                  <Label>Palavras-chave (Keywords)</Label>
                  <Input
                    type="text"
                    value={config.metaKeywords}
                    onChange={(e) => setConfig({ ...config, metaKeywords: e.target.value })}
                  />
                  <HelpText>Separe por vírgulas. Ex: fidelidade, pontos, cashback</HelpText>
                </FormGroup>
              </Grid>

              <FormGroup>
                <Label>Descrição (Meta Description)</Label>
                <TextArea
                  value={config.metaDescription}
                  onChange={(e) => setConfig({ ...config, metaDescription: e.target.value })}
                  maxLength={160}
                />
                <HelpText>Máximo de 160 caracteres. Aparece nos resultados de busca do Google.</HelpText>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>Open Graph (Facebook, LinkedIn, WhatsApp)</SectionTitle>
              <Grid>
                <FormGroup>
                  <Label>Título Open Graph</Label>
                  <Input
                    type="text"
                    value={config.ogTitle}
                    onChange={(e) => setConfig({ ...config, ogTitle: e.target.value })}
                  />
                  <HelpText>Título quando compartilhado em redes sociais</HelpText>
                </FormGroup>

                <FormGroup>
                  <Label>Imagem Open Graph (URL)</Label>
                  <Input
                    type="text"
                    value={config.ogImage}
                    onChange={(e) => setConfig({ ...config, ogImage: e.target.value })}
                  />
                  <HelpText>Recomendado: 1200x630px</HelpText>
                </FormGroup>
              </Grid>

              <FormGroup>
                <Label>Descrição Open Graph</Label>
                <TextArea
                  value={config.ogDescription}
                  onChange={(e) => setConfig({ ...config, ogDescription: e.target.value })}
                />
                <HelpText>Descrição quando compartilhado em redes sociais</HelpText>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>Twitter Card</SectionTitle>
              <FormGroup>
                <Label>Tipo de Card do Twitter</Label>
                <Input
                  type="text"
                  value={config.twitterCard}
                  onChange={(e) => setConfig({ ...config, twitterCard: e.target.value })}
                />
                <HelpText>Valores: summary, summary_large_image, app, player</HelpText>
              </FormGroup>
            </Section>
          </>
        )}

        {activeTab === 'guide' && (
          <>
            <Section>
              <SectionTitle>Como Configurar o Google Analytics</SectionTitle>
              <ol style={{ lineHeight: '2', color: '#4a5568' }}>
                <li>Acesse <LinkButton href="https://analytics.google.com/" target="_blank">Google Analytics <ExternalLink size={14} /></LinkButton></li>
                <li>Crie uma propriedade GA4 para seu site</li>
                <li>Copie o ID de medição (começa com G-)</li>
                <li>Cole o ID no campo acima e salve</li>
                <li>Aguarde 24-48h para começar a ver dados</li>
              </ol>
            </Section>

            <Section>
              <SectionTitle>Como Configurar o Google Tag Manager</SectionTitle>
              <ol style={{ lineHeight: '2', color: '#4a5568' }}>
                <li>Acesse <LinkButton href="https://tagmanager.google.com/" target="_blank">Google Tag Manager <ExternalLink size={14} /></LinkButton></li>
                <li>Crie uma conta e um contêiner</li>
                <li>Copie o ID do contêiner (começa com GTM-)</li>
                <li>Cole o ID no campo acima e salve</li>
                <li>Configure suas tags no painel do GTM</li>
              </ol>
            </Section>

            <Section>
              <SectionTitle>Como Verificar no Google Search Console</SectionTitle>
              <ol style={{ lineHeight: '2', color: '#4a5568' }}>
                <li>Acesse <LinkButton href="https://search.google.com/search-console" target="_blank">Google Search Console <ExternalLink size={14} /></LinkButton></li>
                <li>Adicione sua propriedade (URL do site)</li>
                <li>Escolha o método "Tag HTML"</li>
                <li>Copie apenas o código entre as aspas da tag meta</li>
                <li>Cole o código no campo acima e salve</li>
                <li>Volte ao Search Console e clique em "Verificar"</li>
              </ol>

              <FormGroup>
                <Label>Exemplo do código de verificação:</Label>
                <CodeBlock>
                  <CopyButton onClick={() => copyToClipboard('abc123def456ghi789')}>
                    <Copy size={16} /> Copiar
                  </CopyButton>
                  <code>
                    {`<!-- Você verá isto no Search Console: -->
<meta name="google-site-verification" content="abc123def456ghi789" />

<!-- Cole apenas isto aqui: -->
abc123def456ghi789`}
                  </code>
                </CodeBlock>
              </FormGroup>
            </Section>

            <Section>
              <SectionTitle>Como Configurar o Microsoft Clarity</SectionTitle>
              <ol style={{ lineHeight: '2', color: '#4a5568' }}>
                <li>Acesse <LinkButton href="https://clarity.microsoft.com/" target="_blank">Microsoft Clarity <ExternalLink size={14} /></LinkButton></li>
                <li>Crie uma conta gratuita (se ainda não tiver)</li>
                <li>Clique em "Add new project"</li>
                <li>Insira o nome e URL do seu site</li>
                <li>Copie o Project ID (uma sequência de letras e números)</li>
                <li>Cole o ID no campo acima e salve</li>
                <li>Aguarde alguns minutos para começar a ver dados</li>
              </ol>
              <HelpText style={{ marginTop: '1rem' }}>
                <strong>O que o Clarity oferece gratuitamente:</strong>
                <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem' }}>
                  <li>Gravações de sessões dos usuários</li>
                  <li>Mapas de calor (heatmaps)</li>
                  <li>Análise de comportamento e cliques</li>
                  <li>Identificação de pontos de frustração</li>
                  <li>Sem limite de tráfego</li>
                </ul>
              </HelpText>
            </Section>

            <Section>
              <SectionTitle>Sitemap e Robots.txt</SectionTitle>
              <StatusCard status="success">
                <CheckCircle size={20} />
                Sitemap.xml e robots.txt já estão otimizados e configurados!
              </StatusCard>

              <div style={{ lineHeight: '2', color: '#4a5568', marginTop: '1rem' }}>
                <p><strong>Arquivos criados:</strong></p>
                <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                  <li>
                    <LinkButton href="/sitemap.xml" target="_blank">
                      /sitemap.xml <ExternalLink size={14} />
                    </LinkButton> - Mapa do site para motores de busca
                  </li>
                  <li>
                    <LinkButton href="/robots.txt" target="_blank">
                      /robots.txt <ExternalLink size={14} />
                    </LinkButton> - Instruções para crawlers
                  </li>
                </ul>

                <div style={{ marginTop: '1.5rem' }}>
                  <strong>O que está incluído no sitemap.xml:</strong>
                  <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <li>Landing page principal (prioridade máxima)</li>
                    <li>Páginas de planos e registro</li>
                    <li>Seções importantes (funcionalidades, benefícios, FAQ)</li>
                    <li>Frequência de atualização definida</li>
                    <li>Datas de última modificação</li>
                  </ul>
                </div>

                <div style={{ marginTop: '1.5rem' }}>
                  <strong>O que está configurado no robots.txt:</strong>
                  <ul style={{ marginLeft: '1.5rem', marginTop: '0.5rem' }}>
                    <li>✅ Páginas públicas permitidas para indexação</li>
                    <li>🚫 Áreas privadas bloqueadas (dashboard, admin)</li>
                    <li>🚫 APIs e arquivos sensíveis protegidos</li>
                    <li>✅ Recursos estáticos permitidos (imagens, CSS, JS)</li>
                    <li>🚫 Bots maliciosos bloqueados (scrapers, AhrefsBot)</li>
                    <li>⚡ Crawl-delay configurado para evitar sobrecarga</li>
                  </ul>
                </div>

                <div style={{ marginTop: '1.5rem', padding: '1rem', background: '#f0fdf4', borderRadius: '8px', border: '1px solid #10b981' }}>
                  <strong style={{ color: '#065f46' }}>📌 Próximo passo:</strong>
                  <p style={{ marginTop: '0.5rem', color: '#065f46' }}>
                    Envie o sitemap para o Google Search Console após verificar seu site:
                  </p>
                  <ol style={{ marginLeft: '1.5rem', marginTop: '0.5rem', color: '#065f46' }}>
                    <li>Acesse o Search Console</li>
                    <li>No menu lateral, clique em "Sitemaps"</li>
                    <li>Digite: <code style={{ background: 'white', padding: '2px 6px', borderRadius: '4px' }}>sitemap.xml</code></li>
                    <li>Clique em "Enviar"</li>
                  </ol>
                </div>
              </div>
            </Section>

            <Section>
              <SectionTitle>Testando as Configurações</SectionTitle>
              <div style={{ lineHeight: '2', color: '#4a5568' }}>
                <p><strong>Para testar o Open Graph:</strong></p>
                <LinkButton href="https://developers.facebook.com/tools/debug/" target="_blank">
                  Facebook Sharing Debugger <ExternalLink size={14} />
                </LinkButton>
                <br /><br />
                <p><strong>Para testar o Twitter Card:</strong></p>
                <LinkButton href="https://cards-dev.twitter.com/validator" target="_blank">
                  Twitter Card Validator <ExternalLink size={14} />
                </LinkButton>
                <br /><br />
                <p><strong>Para testar dados estruturados:</strong></p>
                <LinkButton href="https://search.google.com/test/rich-results" target="_blank">
                  Google Rich Results Test <ExternalLink size={14} />
                </LinkButton>
                <br /><br />
                <p><strong>Para validar o sitemap:</strong></p>
                <LinkButton href="https://www.xml-sitemaps.com/validate-xml-sitemap.html" target="_blank">
                  XML Sitemap Validator <ExternalLink size={14} />
                </LinkButton>
                <br /><br />
                <p><strong>Para testar o robots.txt:</strong></p>
                <LinkButton href="https://support.google.com/webmasters/answer/6062598" target="_blank">
                  Google Robots Testing Tool <ExternalLink size={14} />
                </LinkButton>
              </div>
            </Section>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
          <Button onClick={handleSave}>
            <Save size={20} />
            Salvar Configurações
          </Button>
        </div>
      </Container>
    </AdminLayout>
  );
};
